'use client';
import { type CredentialResponse, GoogleLogin } from '@react-oauth/google';
import {
  type OAuthProviderName,
  providerLabel
} from '@shared/all/domains/authMethods';
import { UserType } from '@shared/all/domains/user';
import toast from '@shared/utils/helpers/toast';
import useAuth from '@shared/utils/hooks/useAuth';
import { useEffect, useRef, useState } from 'react';
import AppleSignin, { type AppleAuthResponse } from 'react-apple-signin-auth';

import { spaceConfig } from '../../utils/getSpaceConfig';
import { client, service } from '../../utils/trpc';

// Google caps its rendered button at 400px; measure the column and clamp so it
// fills the same width as the Apple/passkey buttons.
const GOOGLE_MAX_WIDTH = 400;

interface Props {
  onSuccess?: () => void;
  isPrivate?: boolean;
  dividerBackground?: 'primary' | 'secondary' | 'light';
  showDivider?: boolean;
  // Which providers to render. Omitted = both (login shows every provider; the
  // username-first step and Settings pass only the relevant ones).
  providers?: OAuthProviderName[];
  // Link mode: when set, the verified provider token is handed here instead of
  // signing in. Settings uses this to attach a provider to the current account.
  onToken?: (
    provider: OAuthProviderName,
    idToken: string,
    user?: { email?: string }
  ) => Promise<void>;
}

const dividerBgClasses = {
  primary: 'bg-background-primary',
  secondary: 'bg-background-secondary',
  light: 'bg-background-light'
} as const;

const OAuthButtons = ({
  onSuccess,
  isPrivate = false,
  dividerBackground = 'secondary',
  showDivider = true,
  providers,
  onToken
}: Props) => {
  const { initializeUser } = useAuth({ client, service });
  const state = Math.random().toString(36).substring(2, 15);

  const showGoogle = !providers || providers.includes('GOOGLE');
  const showApple = !providers || providers.includes('APPLE');

  // Shared for both providers. Link mode hands the token off; login mode tries
  // factiii's multi-provider path first (covers a secondarily-linked provider
  // the package would reject) then the package for brand-new signup.
  const handleProviderToken = async (
    provider: OAuthProviderName,
    idToken: string,
    user?: { email?: string }
  ) => {
    if (onToken) {
      await onToken(provider, idToken, user);
      return;
    }

    // oAuthLogin resolves any linked provider (multi-provider) and creates a new
    // account when the identity is unknown, so one call covers every case.
    const [error, data] = await client.auth.oAuthLogin.mutate({
      idToken,
      instanceId: spaceConfig.space.id,
      provider,
      user,
      types: isPrivate ? [UserType.PRIVATE] : []
    });
    if (error) {
      toast(
        `Failed to sign in with ${providerLabel(provider)}. Please try again.`,
        '',
        'error'
      );
    } else if (data?.success) {
      localStorage.removeItem('basicGuideCompleted');
      await initializeUser();
      toast('Successfully signed in!');
      onSuccess?.();
    }
  };

  // Google's widget only takes a pixel width, so measure the button column and
  // feed it that (clamped to Google's 400px max) — keeps it flush with the
  // full-width Apple/passkey buttons and re-measures on resize.
  const columnRef = useRef<HTMLDivElement>(null);
  const [googleWidth, setGoogleWidth] = useState(0);
  useEffect(() => {
    const el = columnRef.current;
    if (!el) return;
    const measure = () =>
      setGoogleWidth(Math.min(GOOGLE_MAX_WIDTH, Math.round(el.offsetWidth)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleGoogleSuccess = async (response: CredentialResponse) => {
    if (!response.credential) {
      toast('No ID token received from Google.', '', 'error');
      return;
    }
    await handleProviderToken('GOOGLE', response.credential);
  };

  const handleAppleSuccess = async (response: AppleAuthResponse) => {
    if (!response.authorization?.id_token) {
      throw new Error('No ID token received from Apple');
    }
    if (response.authorization.state !== state) {
      throw new Error('Invalid state');
    }
    await handleProviderToken(
      'APPLE',
      response.authorization.id_token,
      response.user || undefined
    );
  };

  const handleAppleError = (error: unknown) => {
    console.error('Apple OAuth error:', error);
    toast('Failed to sign in with Apple. Please try again.', '', 'error');
  };

  return (
    <div className="space-y-4">
      {showDivider && (
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-ds-border-standard" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span
              className={`${dividerBgClasses[dividerBackground]} px-3 text-textSecondary`}
            >
              Or continue with
            </span>
          </div>
        </div>
      )}

      <div ref={columnRef} className="w-full space-y-3">
        {/* Both providers render third-party widgets — Google's is a cross-origin
            iframe — so neither has text a test can find. These wrappers are how
            the e2e suite checks that a deployment is still OFFERING each provider:
            a missing client id makes the button vanish with no error anywhere,
            and nobody notices until someone cannot sign in. */}
        {showGoogle && googleWidth > 0 && (
          <div data-testid="oauth-google">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() =>
                toast(
                  'Failed to sign in with Google. Please try again.',
                  '',
                  'error'
                )
              }
              width={googleWidth}
              text="continue_with"
              shape="rectangular"
              theme="outline"
            />
          </div>
        )}
        {showApple && (
          <div data-testid="oauth-apple">
            <AppleSignin
              authOptions={{
                clientId: process.env.NEXT_PUBLIC_APPLE_CLIENT_ID!,
                scope: 'email',
                redirectURI:
                  typeof window !== 'undefined' ? window.location.origin : '',
                state,
                usePopup: true
              }}
              uiType="dark"
              className="w-full"
              buttonExtraChildren="Continue with Apple"
              onSuccess={handleAppleSuccess}
              onError={handleAppleError}
              noDefaultStyle={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OAuthButtons;
